import { PageCanvas } from "@/components/page-canvas";
import { Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import JobWorkspace from "@/components/job-workspace";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  RecordDescription,
  RecordLink,
  RecordMetadata,
  RecordTitle,
} from "@/components/record-link";
import {
  locationJobPath,
  locationJobProductConsumptionsPath,
  newLocationJobProductConsumptionPath,
  locationDetailingProductPath,
} from "@/routes";
import { quantity } from "../inventory/types";
import { dateTime } from "../jobs/types";

type Consumption = {
  id: string;
  product_id: string;
  name: string;
  quantity: string;
  unit: "litre" | "piece";
  reason: string;
  creator: string | null;
  created_at: string;
};
export default function Consumptions({
  job_id,
  consumptions,
  pagination,
}: {
  job_id: string;
  consumptions: Consumption[];
  pagination: { page: number; pages: number };
}) {
  const { auth, i18n } = usePage().props;
  const { t } = useTranslation();
  if (!auth.location) return null;
  const locationId = auth.location.id;
  return (
    <JobWorkspace
      primaryAction={
        <Button
          render={
            <Link
              href={newLocationJobProductConsumptionPath(locationId, job_id)}
            />
          }
        >
          {t("product_consumptions.record")}
        </Button>
      }
      breadcrumbs={[
        {
          title: t("jobs.confirmed"),
          href: locationJobPath(locationId, job_id),
        },
      ]}
    >
      <Head title={t("product_consumptions.title")} />
      <PageCanvas padded={false}>
        <h2 className="text-page font-display">
          {t("product_consumptions.title")}
        </h2>
        <p className="text-muted-foreground text-sm">
          {t("product_consumptions.help")}
        </p>
        {consumptions.length === 0 && <p>{t("product_consumptions.empty")}</p>}
        <ol className="space-y-4">
          {consumptions.map((entry) => (
            <li key={entry.id}>
              <Card>
                <CardContent className="space-y-2 pt-6 break-words">
                  <RecordLink
                    href={locationDetailingProductPath(
                      locationId,
                      entry.product_id,
                    )}
                  >
                    <RecordTitle>{entry.name}</RecordTitle>
                  </RecordLink>
                  <RecordDescription>
                    <span className="font-semibold tabular-nums">
                      {quantity(entry.quantity, i18n.locale)}{" "}
                      {t(`inventory.${entry.unit}`)}
                    </span>
                  </RecordDescription>
                  <RecordDescription>{entry.reason}</RecordDescription>
                  <RecordMetadata>
                    {entry.creator} ·{" "}
                    {dateTime(entry.created_at, i18n.time_zone, i18n.locale)}
                  </RecordMetadata>
                </CardContent>
              </Card>
            </li>
          ))}
        </ol>
        {pagination.pages > 1 && (
          <nav
            className="flex flex-wrap gap-4"
            aria-label={t("product_consumptions.title")}
          >
            {pagination.page > 1 && (
              <Link
                className="underline"
                href={locationJobProductConsumptionsPath(locationId, job_id, {
                  page: pagination.page - 1,
                })}
              >
                {t("customers.previous")}
              </Link>
            )}
            {pagination.page < pagination.pages && (
              <Link
                className="underline"
                href={locationJobProductConsumptionsPath(locationId, job_id, {
                  page: pagination.page + 1,
                })}
              >
                {t("customers.next")}
              </Link>
            )}
          </nav>
        )}
        <Link
          className="block underline"
          href={locationJobPath(locationId, job_id)}
        >
          {t("jobs.confirmed")}
        </Link>
      </PageCanvas>
    </JobWorkspace>
  );
}
